"use client"

import React, { createContext, useContext, useState, useEffect, ReactNode } from "react"
import { getApiBaseUrl } from "@/lib/api"

export interface TenantConfig {
  id: number
  name: string
  slug: string
  domain: string
  custom_domain: string | null
  logo_url: string
  favicon_url: string
  primary_color: string
  accent_color: string
  plan: "basic" | "pro" | "enterprise"
  features: Record<string, boolean>
}

interface TenantContextType {
  tenant: TenantConfig | null
  isLoading: boolean
  error: string | null
  hasFeature: (feature: string) => boolean
}

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  isLoading: true,
  error: null,
  hasFeature: () => false,
})

const CACHE_KEY = "promobot_tenant_config"
const CACHE_TTL_MS = 5 * 60 * 1000

function getCachedTenant(): TenantConfig | null {
  if (typeof window === "undefined") return null
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const { data, timestamp } = JSON.parse(raw)
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(CACHE_KEY)
      return null
    }
    return data
  } catch {
    return null
  }
}

function cacheTenant(data: TenantConfig) {
  if (typeof window === "undefined") return
  localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }))
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<TenantConfig | null>(getCachedTenant)
  const [isLoading, setIsLoading] = useState(!getCachedTenant())
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchTenantConfig() {
      try {
        const domain = window.location.hostname
        const baseUrl = getApiBaseUrl()
        const res = await fetch(`${baseUrl}/api/tenant/config/?domain=${domain}`)

        if (!res.ok) {
          if (res.status === 404) {
            setError("Tenant não encontrado")
          } else {
            setError(`Erro ao carregar configuração (${res.status})`)
          }
          setIsLoading(false)
          return
        }

        const data: TenantConfig = await res.json()
        setTenant(data)
        cacheTenant(data)
        applyTenantBranding(data)
      } catch (err) {
        setError("Erro de conexão com o servidor")
      } finally {
        setIsLoading(false)
      }
    }

    fetchTenantConfig()
  }, [])

  useEffect(() => {
    if (tenant) applyTenantBranding(tenant)
  }, [tenant])

  const hasFeature = (feature: string): boolean => {
    if (!tenant) return false
    return tenant.features[feature] ?? false
  }

  return (
    <TenantContext.Provider value={{ tenant, isLoading, error, hasFeature }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenant() {
  return useContext(TenantContext)
}

function applyTenantBranding(tenant: TenantConfig) {
  if (typeof document === "undefined") return

  const root = document.documentElement

  if (tenant.primary_color) {
    root.style.setProperty("--tenant-primary", tenant.primary_color)
  }
  if (tenant.accent_color) {
    root.style.setProperty("--tenant-accent", tenant.accent_color)
  }

  if (tenant.favicon_url) {
    let favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement
      || document.querySelector("link[rel='shortcut icon']") as HTMLLinkElement
    if (favicon) {
      favicon.href = tenant.favicon_url
    } else {
      favicon = document.createElement("link")
      favicon.rel = "icon"
      favicon.href = tenant.favicon_url
      document.head.appendChild(favicon)
    }
  }

  if (tenant.name) {
    document.title = `${tenant.name} Dashboard`
  }
}
